import PropTypes from "prop-types";

export const CTA_BEHAVIOR = {
  DISABLED: "disabled",
  HIDDEN: "hidden",
};

const BADGE_CONFIG = {
  in_progress: { label: "In Progress", className: "tw-text-semantic-green-100" },
  ready_to_close: { label: "Ready to Close", className: "tw-text-semantic-green-100" },
  closed: { label: "Closed", className: "tw-text-theme-text-button-color" },
};

const ERROR_BADGE = { label: "Error", className: "tw-text-semantic-red-100" };

export const stageBadge = (stage) => {
  if (stage?.errorTitle) return ERROR_BADGE;
  return BADGE_CONFIG[stage?.badge] || null;
};

const STAGE_CONTENT = {
  request_received: {
    subtitle: "Request Received - Under Review",
    description:
      "Your title order has been submitted to Blueprint. They're reviewing the details and will begin processing once confirmed.",
    metadataLabel: "Last Submitted:",
    cta: CTA_BEHAVIOR.DISABLED,
  },
  order_opening: {
    subtitle: "Order Opening in Progress",
    description: "Blueprint is initiating the order. Expect updates here as each milestone is reached",
    metadataLabel: "Last Updated:",
    cta: CTA_BEHAVIOR.HIDDEN,
  },
};

// Statuses that draw no milestone ladder: the card falls back to the onboarding one and the
// status is carried by a label next to the date instead.
//
// Declined and cancelled end the order, so the CTA stays usable — opening a new one is the way
// forward. On Hold is paused, not over: Blueprint resumes it by changing that order's status.
// Submitting again would POST a fresh transaction and overwrite blueprint_order_id
// (CreateTransactionService#call), orphaning the order that is meant to come back — so On Hold
// keeps the label but disables the CTA.
export const STATUS_LABEL_STAGES = {
  declined: { label: "Declined", cta: null },
  // The key is Blueprint's status, spelled their way; the label is ours, spelled the way the rest
  // of the app and the design spell it.
  cancelled: { label: "Canceled", cta: null },
  on_hold: { label: "On Hold", cta: CTA_BEHAVIOR.DISABLED },
};

export const stageStatusLabel = (stage) => STATUS_LABEL_STAGES[stage?.crmTransactionStatus]?.label || null;

export const stageContentFor = (stage) => STAGE_CONTENT[stage?.crmTransactionStatus] || null;

export const stageCtaBehavior = (stage) => {
  if (stage?.errorTitle) return CTA_BEHAVIOR.HIDDEN;
  if (stageBadge(stage)) return CTA_BEHAVIOR.HIDDEN;

  const statusLabelStage = STATUS_LABEL_STAGES[stage?.crmTransactionStatus];
  if (statusLabelStage) return statusLabelStage.cta;

  return stageContentFor(stage)?.cta || null;
};

const statusShape = PropTypes.shape({
  value: PropTypes.string,
  title: PropTypes.string,
});

const milestoneShape = PropTypes.shape({
  key: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  status: PropTypes.string,
});

const stageShape = PropTypes.shape({
  crmTransactionStatus: PropTypes.string.isRequired,
  crmDescription: PropTypes.string,
  lastUpdated: PropTypes.string,
  badge: PropTypes.string,
  errorTitle: PropTypes.string,
  errorMessage: PropTypes.string,
  escrowOfficer: PropTypes.shape({ name: PropTypes.string }),
  titleStatus: statusShape,
  earnestMoneyStatus: statusShape,
  estimatedSigning: statusShape,
  cdaStatus: statusShape,
  fundingStatus: statusShape,
  recordingStatus: statusShape,
  primaryMilestone: milestoneShape,
  milestones: PropTypes.arrayOf(milestoneShape),
  progress: PropTypes.shape({
    current_milestone_index: PropTypes.number,
    total_milestones: PropTypes.number,
    percent_complete: PropTypes.number,
  }),
});

export default stageShape;
